import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Leaf, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Optimize image URLs for better performance
const optimizeImageUrl = (url, width = 600) => {
  if (!url) return url;
  // For Unsplash images, add optimization parameters
  if (url.includes('unsplash.com') && !url.includes('?')) {
    return `${url}?w=${width}&q=75&fm=webp&fit=crop`;
  }
  return url;
};

export default function ProductsPage() {
  const { t, language } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Fetch products from API
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BACKEND_URL}/api/products`);
        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }
        const data = await response.json();
        setProducts(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-primary to-[hsl(42,100%,65%)] text-white py-20">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center animate-fade-in">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">
              <Leaf className="w-4 h-4 mr-2" />
              {t('products.natural')}
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              {t('products.title')}
            </h1>
            <p className="text-lg text-white/90">
              {t('products.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="section-padding">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              <p className="text-lg text-muted-foreground mt-4">Loading products...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-lg text-destructive">Error: {error}</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-lg text-muted-foreground">{t('products.noProducts')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map((product, index) => (
                <Card 
                  key={product.id} 
                  className="card-hover overflow-hidden bg-card border-border shadow-md"
                  style={{
                    animationDelay: `${index * 100}ms`,
                    animation: 'slideUp 0.6s ease-out forwards',
                    opacity: 0
                  }}
                >
                  <div className="aspect-square overflow-hidden bg-secondary/50">
                    <img 
                      src={optimizeImageUrl(product.image)} 
                      alt={`${product.name[language] || product.name.en} - Prirodan Med - Organic Honey Serbia`}
                      title={product.name[language] || product.name.en}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                      loading="lazy"
                      decoding="async"
                      width="400"
                      height="400"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1587049352851-8d4e89133924?w=400&q=75&fm=webp';
                      }}
                    />
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-xl font-semibold text-foreground">
                        {product.name[language] || product.name.en}
                      </h3>
                      <Badge variant="secondary" className="ml-2 bg-primary/10 text-primary border-primary/20">
                        <Leaf className="w-3 h-3 mr-1" />
                        Organic
                      </Badge>
                    </div>
                    <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                      {product.description[language] || product.description.en}
                    </p>
                  </CardContent>
                  <CardFooter className="p-6 pt-0 flex items-center justify-between">
                    <span className="text-xl font-bold text-primary">
                      {product.price}
                    </span>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="section-padding bg-secondary/30">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <Card className="max-w-4xl mx-auto bg-card border-primary/20 shadow-lg">
            <CardContent className="p-8 sm:p-12">
              <div className="text-center mb-8">
                <div className="inline-block p-4 bg-primary/10 rounded-full mb-6">
                  <Leaf className="w-12 h-12 text-primary" />
                </div>
                <h2 className="text-3xl font-bold mb-4 text-foreground">
                  {t('products.whyChoose')}
                </h2>
              </div>
              <div className="grid md:grid-cols-2 gap-6 text-sm">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">{t('products.natural')}:</strong> {t('products.naturalDesc')}
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">{t('products.organic')}:</strong> {t('products.organicDesc')}
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">{t('products.family')}:</strong> {t('products.familyDesc')}
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">{t('products.tested')}:</strong> {t('products.testedDesc')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}